import { CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, FormControl, FormLabel, Image, Input, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react'
import { FormEvent, useState } from 'react'
import { uploadFile } from '../pages/systemForm.support'
import { FileViewTypeBackEndKey, FileViewTypeEnum } from '../commons/common'
import { getEnvironmentURL } from '../commons/env'

interface response {
  url: string
  cdn_url: string
  file_id: string
}
export interface URLType {
  is_private_file?: boolean
  cdn_url: string
  id?: string
}
export const SingleFileUpload = ({
  title,
  required,
  placeHolder,
  testId,
  name,
  defaultValue,
  callBack
}: {
  title: string
  required: boolean
  placeHolder: string
  testId: string
  name: string
  defaultValue: URLType
  callBack: (url: URLType) => void
}): JSX.Element => {
  const [fileLoading, setFileLoading] = useState<boolean>(false)
  const [fileURL, setFileURL] = useState<string>(defaultValue?.cdn_url)
  const [fileID, setFileID] = useState<string>(defaultValue?.id ?? '')
  const [fileValue, setFileValue] = useState<string>('')
  const [fileType, setFileType] = useState<FileViewTypeEnum>(defaultValue?.is_private_file ? FileViewTypeEnum.PRIVATE : FileViewTypeEnum.PUBLIC)
  const handleChange = (event: FormEvent, callBack: (url: URLType) => void): void => {
    setFileLoading(true)
    setFileURL('')
    setFileID('')
    const HTMLFileElement = event.target as HTMLInputElement
    const currentFile = HTMLFileElement.files ? HTMLFileElement.files[0] : null

    const reader = new FileReader()
    if (currentFile !== null && reader !== null) {
      reader.readAsArrayBuffer(currentFile)

      reader.onload = () => {
        const formData = new FormData()
        formData.append(FileViewTypeBackEndKey, fileType)
        formData.append('doc', currentFile as Blob)

        uploadFile(formData)
          .then(res => {
            const response = res as response
            callBack({
              cdn_url: response?.cdn_url ?? 'Error',
              is_private_file: fileType === FileViewTypeEnum.PRIVATE,
              id: response?.file_id
            })
            setFileLoading(false)
            setFileURL(response?.cdn_url)
            setFileID(response?.file_id ?? '')
          })
          .catch(response => {
            setFileURL('')
            setFileID('')
            setFileLoading(false)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
            return response.errorMessage ?? 'error'
          })
      }
    }
  }
  const clearUpload = (): void => {
    // Show confirmation dialog before deleting
    const confirmDelete = window.confirm('Are you sure you want to delete this file?')

    if (confirmDelete) {
      // Make API call to delete file if fileID exists
      if (fileID) {
        const deleteFileURL = getEnvironmentURL.fileDelete(fileID)
        fetch(deleteFileURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(res => {
            if (res.ok) {
              // Clear local state regardless of confirmation or API result
              setFileURL('')
              setFileID('')
              setFileValue('')
              callBack({ cdn_url: '', is_private_file: fileType === FileViewTypeEnum.PRIVATE, id: '' })
              console.log('File deleted successfully')
            } else {
              console.error('Failed to delete file')
            }
          })
          .catch(error => {
            console.error('Error deleting file:', error)
          })
      }
    }
  }

  const handleFileTypeChange = (value: string) => {
    if (fileType !== value) {
      // Only show confirmation when fileID is available (existing file)
      if (fileID) {
        const confirmChange = window.confirm('Are you sure you want to change the file visibility?')
        if (!confirmChange) {
          return
        }
      }

      // Make API call to move file if user confirms and file_id exists
      if (fileID) {
        const moveFileURL = getEnvironmentURL.fileMove(fileID)
        fetch(moveFileURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            is_private_file: value === FileViewTypeEnum.PRIVATE
          })
        })
          .then(res => res.json())
          .then(data => {
            console.log('File moved successfully:', data)
            // Extract new CDN URL from response and update fileURL
            if (data?.newCdnUrl) {
              setFileURL(data.newCdnUrl)
            }
          })
          .catch(error => {
            console.error('Error moving file:', error)
          })
      }
    }
    setFileType(value as FileViewTypeEnum)
    callBack({ cdn_url: fileURL, is_private_file: value === FileViewTypeEnum.PRIVATE, id: fileID })
  }
  return (
    <FormControl p="2">
      <FormLabel fontSize={'14px'} fontWeight="bold">
        {title} {required && <sup color="red">*</sup>}
      </FormLabel>
      <Box>
        <RadioGroup value={fileType} onChange={handleFileTypeChange}>
          <Stack direction="row" spacing={4} mb="2">
            <Radio value={FileViewTypeEnum.PRIVATE} colorScheme="green">
              Private
            </Radio>
            <Radio value={FileViewTypeEnum.PUBLIC} colorScheme="green">
              Public
            </Radio>
          </Stack>
        </RadioGroup>
      </Box>
      <Input
        alignItems={'center'}
        justifyContent="center"
        display="flex"
        placeholder={placeHolder}
        data-testid={testId}
        fontSize="12px"
        name={name}
        m="0"
        mb="0"
        type="file"
        value={fileValue}
        onChange={event => {
          handleChange(event, (result: URLType) => {
            callBack(result)
          })
        }}
      />
      <Flex mb="4" m="2" alignItems={'center'}>
        {fileLoading && <Text>Uploading..</Text>}
        <a
          title={fileURL}
          style={{ fontSize: '11px', height: '20px', overflow: 'hidden', textOverflow: 'ellipsis' }}
          href={fileURL}
          rel="noreferrer"
          target={'_blank'}
        >
          {fileURL}
        </a>
        {fileURL && (
          <>
            {fileURL.toLowerCase().endsWith('.pdf')
              ? <Box w="50px" h="50px" borderRadius={'8px'} borderColor="#000" borderWidth="2px" display="flex" alignItems="center" justifyContent="center" bg="red.50" fontSize="10px" fontWeight="bold" color="red.500">PDF</Box>
              : <Image w="50px" h="50px" borderRadius={'8px'} borderColor="#000" borderWidth="2px" src={fileURL} />
            }
            <Button ml="2" p="2" m="1" minW={'5'} minH={'5'} h={'8'} bg={'gray.300'} colorScheme="orange" onClick={clearUpload}>
              <CloseIcon boxSize={2} />
            </Button>
          </>
        )}
      </Flex>
    </FormControl>
  )
}
